import React, { useEffect, useState } from "react";
import useUpdateSearchParams from "../../hooks/useUpdateSearchParams";

import styles from "./AnalyticsDashboard.module.css";
import FilterBar from "./FilterBar";
import OverviewSection from "./sections/OverviewSection";
import ContentSection from "./sections/ContentSection";
import UsersSection from "./sections/UsersSection";
import GeoSection from "./sections/GeoSection";
import RealtimeSection from "./sections/RealtimeSection";
import { getPlatformAnalytics } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";

const TABS = ["Overview", "Content", "Users", "Geography", "Real-time"];

// Old tab name from before the redesign, so `?tab=Videos` bookmarks/links
// still land on the section that replaced it instead of rendering nothing.
const LEGACY_TAB_ALIASES = { Videos: "Content" };

export default function AnalyticsDashboard() {
  const [searchParams, updateParams] = useUpdateSearchParams();
  const rawTab = searchParams.get("tab") || "Overview";
  const activeTab = LEGACY_TAB_ALIASES[rawTab] || rawTab;
  const [platforms, setPlatforms] = useState([]);

  // Not useUrlTab: the dashboard has always pushed history here (Back steps
  // between sections, like its filters), and it needs the legacy alias above.
  const handleTabChange = (tab) => updateParams({ tab });

  useEffect(() => {
    (async () => {
      try {
        const response = await getPlatformAnalytics();
        setPlatforms(response?.data?.data?.platforms || []);
      } catch (err) {
        showToast(err?.response?.data?.message || "Failed to load platforms", "error");
      }
    })();
  }, []);

  // Every chart here is a Recharts <ResponsiveContainer>, which measures its
  // own pixel width via ResizeObserver on mount. Printing swaps the page into
  // a differently-sized print layout, but that swap doesn't fire a `resize`
  // event on its own -- so charts can still be holding whatever (possibly
  // stale/zero) width they last measured on screen, and render as empty SVGs
  // in the PDF ("blank data") even though the KPI numbers/tables print fine.
  // `matchMedia('print')` fires in sync with the actual print layout switch,
  // so dispatching a resize event there gives ResizeObserver a real width to
  // measure before the page is rasterized.
  useEffect(() => {
    const mql = window.matchMedia("print");
    const forceResize = () => window.dispatchEvent(new Event("resize"));
    mql.addEventListener ? mql.addEventListener("change", forceResize) : mql.addListener(forceResize);
    window.addEventListener("beforeprint", forceResize);
    return () => {
      mql.removeEventListener ? mql.removeEventListener("change", forceResize) : mql.removeListener(forceResize);
      window.removeEventListener("beforeprint", forceResize);
    };
  }, []);

  return (
    <div className={styles.MainContainer}>
      <main className={styles.main}>
        <section className={styles.container}>
          <div className={styles.headerRow}>
            <h1 className={styles.pageTitle}>Analytics</h1>
            <div className={styles.tabs}>
              {TABS.map((tab) => (
                <button
                  key={tab}
                  type="button"
                  className={activeTab === tab ? styles.tabActive : undefined}
                  onClick={() => handleTabChange(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>
          </div>

          <FilterBar platforms={platforms} />

          {activeTab === "Overview" && <OverviewSection />}
          {activeTab === "Content" && <ContentSection />}
          {activeTab === "Users" && <UsersSection />}
          {activeTab === "Geography" && <GeoSection />}
          {activeTab === "Real-time" && <RealtimeSection />}
        </section>
      </main>
    </div>
  );
}
